import type { AuditEvent } from '../../api/types';

/**
 * The session a row belongs to, if it belongs to one.
 *
 * gdsgate writes the id into the detail of everything a session does — opened,
 * activity, closed, the recording it produced — and never as a field of its own
 * on the wire. So it comes out of the detail, and the detail comes in two forms
 * that are both permanent:
 *
 * * **an object**, which is what a current cluster seals:
 *   `{"session_uid":"<id>","kind":"ssh",…}`. One spelling, `session_uid`, on
 *   every lane — the key is read, not searched for;
 * * **`key=value` text**, which is what older rows are and will always be, in
 *   the two spellings those rows used: `session_uid=<id>;bytes=0` around a
 *   session, and `session=<id>;call=1` on the `llm.call` rows inside it. A
 *   console knowing only the first drew a session with its middle missing — the
 *   open, the close, and none of the work.
 *
 * **Which form is read off the row, not off the tenancy.** One console serves
 * tenancies of both versions at once, and a single tenancy's chain crosses the
 * upgrade in the middle: the rows before it stay text forever. Trying the new
 * shape and falling back to the old would be right until the first row that
 * parses as both and means different things by it.
 *
 * In the object the value is taken as the field says it — that is what a field
 * is for. In the text it is still read conservatively, because there the id has
 * to be found rather than looked up: letters, digits and dashes, starting where
 * the key starts, so `parent_session=x` is not mistaken for a session of `x`.
 */
export function sessionOf(event: AuditEvent): string | undefined {
  const structured = asObject(event.detail);
  if (structured !== undefined) {
    const uid = structured['session_uid'];
    return typeof uid === 'string' && uid !== '' ? uid : undefined;
  }
  const match = /(?:^|[;\s])session(?:_uid)?=([A-Za-z0-9-]+)/.exec(event.detail);
  return match?.[1];
}

/**
 * A detail that is a JSON object, as an object — or nothing.
 *
 * **The one test that tells the two forms of a detail apart**, and every reader
 * of a detail goes through it. gdsgate makes the same test on its own side, for
 * the same reason: a detail that is one whole JSON object is the current form,
 * and everything else — `key=value` text, a detail the server had to cut, an
 * empty one — is read as text and reads correctly as text.
 *
 * Nothing for JSON that is not an object either: what is wanted is a bag of
 * named fields, and an array or a bare string is not one. Never throws; a chain
 * is somebody else's output and a console that fell over on a malformed row
 * would be showing nothing instead of showing the rest.
 */
export function asObject(detail: string): Record<string, unknown> | undefined {
  if (!detail.startsWith('{')) return undefined;
  try {
    const parsed: unknown = JSON.parse(detail);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The detail as the fields it is made of — or nothing, for a detail that is not
 * an object and has no fields to show.
 *
 * **In the order the cluster wrote them.** gdsgate fixes the key order of a
 * detail on purpose: those bytes go into the hash chain, so the order is part
 * of what was sealed, and it puts what a row is about first. `JSON.parse` keeps
 * that order, so a reader sees the row as it was written rather than in an
 * alphabet this console chose.
 *
 * Values are shown as they arrived. A number, a list or a boolean is printed as
 * its JSON — `["read","write"]` is the answer, and `read,write` would be a
 * different one.
 */
export function detailFields(detail: string): { key: string; value: string }[] | undefined {
  const structured = asObject(detail);
  if (structured === undefined) return undefined;
  return Object.entries(structured).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : JSON.stringify(value),
  }));
}

/**
 * Every row of one session, oldest first.
 *
 * **Out of what was loaded, and no further.** A page of the chain is a window
 * on its end, so a session that began before that window shows only the part
 * inside it. The screen says so beside the timeline: one that looked complete
 * and was not would be worse than none.
 */
export function sessionTimeline(events: readonly AuditEvent[], uid: string): AuditEvent[] {
  return events.filter((event) => sessionOf(event) === uid).sort((a, b) => a.at - b.at);
}
